<script>
import { findRealParent } from 'vue2-leaflet';
import './L.CustomSVG';
import { computeStandaloneTrackerBounds, computeStandalonePanelBounds, buildPanelPolygon, buildPanelPolyline, shadeBlendConvert, armErrors } from './helpers';

export default {
  name: 'LStandaloneTracker',
  props: {
    tracker: {
      type: Object,
      required: true
    },
    highlighted: {
      type: Boolean,
      required: true
    },
    selected: {
      type: Boolean,
      required: true
    },
    trackerStatus: {
      type: Object,
      required: true
    },
    infoActive: {
      type: Boolean,
      default: false
    },
    angle: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      trackerId: `t_${this.tracker.gcu_code}_${this.tracker.lcu_code}`,
      ready: false
    }
  },
  computed: {
    panelBoundsArray() {
      return computeStandalonePanelBounds(this.tracker.arms);
    },
    trackerBounds() {
      return computeStandaloneTrackerBounds(this.panelBoundsArray);
    },
    panelPointsArray() {
      const panelPointsArray = [];
      this.tracker.arms.forEach((arm, index) => {
        panelPointsArray.push(buildPanelPolygon(this.trackerBounds, this.panelBoundsArray[index]));
      });

      return panelPointsArray;
    },
    sizeAtRest() {
      const minMaxPoints = this.panelPointsArray.reduce(
        function(acc, panelPolygon) {
          let minMax = panelPolygon.reduce(
            function(acc, p) {
              return {
                min: {
                  x: Math.min(acc.min.x, p.x),
                  y: Math.min(acc.min.y, p.y)
                },
                max: {
                  x: Math.max(acc.max.x, p.x),
                  y: Math.max(acc.max.y, p.y)
                }
              };
            },
            {
              min: { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY },
              max: { x: Number.NEGATIVE_INFINITY, y: Number.NEGATIVE_INFINITY }
            }
          );

          return {
            min: {
              x: Math.min(acc.min.x, minMax.min.x),
              y: Math.min(acc.min.x, minMax.min.y)
            },
            max: {
              x: Math.max(acc.max.x, minMax.max.x),
              y: Math.max(acc.max.y, minMax.max.y)
            }
          };
        },
        {
          min: { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY },
          max: { x: Number.NEGATIVE_INFINITY, y: Number.NEGATIVE_INFINITY }
        }
      );

      return { width: minMaxPoints.max.x - minMaxPoints.min.x, height: minMaxPoints.max.y - minMaxPoints.min.y };
    },
    fillTrackerColor() {
      if (this.trackerStatus && this.trackerStatus.status) {
        if (this.trackerStatus.lastStatusCollection === -1 || this.trackerStatus.lastStatusCollection > 600) return '#777777';

        switch (this.trackerStatus.status.code) {
          case 3:
            return '#e1f7d5';
          case 4:
            return '#f0ad4e';
          case 8:
            return '#ccc9c9';
          case 7:
            return '#337ab7';
          case 13:
            return '#ff0000';
          default:
            return '#7F5839';
        }
      } else {
        return '#000000';
      }
    },
    viewState() {
      if (this.highlighted) return 0;
      else if (this.selected) return 1;
      else return 2;
    }
  },
  methods: {
    pointsToString: function(points) {
      let str = "";
      points.forEach(function(p) {
        const a = p.x + "," + p.y;
        str = str + a + " ";
      });

      return str;
    },
    updatePanelView() {
      if (!this.mapObject) return;

      const val = this.viewState;

      if (this.trackerStatus && this.trackerStatus.status) {
        if (this.trackerStatus.armErrors.length === 4 && this.trackerStatus.lastStatusCollection <= 600) {
          this.tracker.arms.forEach((arm) => {
            let errorCode = this.trackerStatus.armErrors[arm.pos - 1].code;
            if (!armErrors[errorCode]) errorCode = 'other';

            if (val === 0) this.mapObject.setSVGElementStyle(`panel${arm.pos}`, `fill: ${shadeBlendConvert(0.2, `#${armErrors[errorCode]}`, '#000000')}`);
            else if (val === 1) this.mapObject.setSVGElementStyle(`panel${arm.pos}`, `fill: ${shadeBlendConvert(0.5, `#${armErrors[errorCode]}`, '#000000')}`);
            else this.mapObject.setSVGElementStyle(`panel${arm.pos}`, `fill: #${armErrors[errorCode]}`);
          });
        } else {
          this.tracker.arms.forEach((arm) => {
            if (val === 0) this.mapObject.setSVGElementStyle(`panel${arm.pos}`, `fill: ${shadeBlendConvert(0.2, '#aaaaaa', '#000000')}`);
            else if (val === 1) this.mapObject.setSVGElementStyle(`panel${arm.pos}`, `fill: ${shadeBlendConvert(0.5, '#aaaaaa', '#000000')}`);
            else this.mapObject.setSVGElementStyle(`panel${arm.pos}`, 'fill: #aaaaaa');
          });
        }
      } else {
        this.tracker.arms.forEach((arm) => {
          if (val === 0) this.mapObject.setSVGElementStyle(`panel${arm.pos}`, 'fill: #FF9E1B');
          else if (val === 1) this.mapObject.setSVGElementStyle(`panel${arm.pos}`, 'fill: #FFFF00');
          else this.mapObject.setSVGElementStyle(`panel${arm.pos}`, 'fill: #484b60');
        });
      }
    },
    unbindTooltip () {
      const tooltip = this.mapObject ? this.mapObject.getTooltip() : null;
      if (tooltip) {
        this.mapObject.unbindTooltip();
      }
    },
    unbindPopup () {
      const popup = this.mapObject ? this.mapObject.getPopup() : null;
      if (popup) {
        this.mapObject.unbindPopup();
      }
    }
  },
  render(createElement) {
    const polygons = [];

    const polylineString = this.pointsToString(buildPanelPolyline(this.panelPointsArray));
    const polyline = createElement('polyline', {
      attrs: {
        'stroke-width': '0.6',
        stroke: this.fillTrackerColor,
        opacity: 0.6,
        points: polylineString,
      }
    });

    polygons.push(polyline);

    this.tracker.arms.forEach((arm, index) => {
        const pointsString = this.pointsToString(this.panelPointsArray[index]);
        const polygon = createElement('polygon', {
            attrs: {
                'stroke-width': '0.1',
                stroke: '#000000',
                points: pointsString,
                class: `panel${arm.pos}`
            }
        });

        polygons.push(polygon);
    });

    let svgElement;
    if (this.ready && this.$slots.default) {
      const slotElement = createElement('div', { style: { display: 'none' }, attrs: { xmlns: 'http://www.w3.org/1999/xhtml'} }, this.$slots.default);
      const foreignObjectElement = createElement('foreignObject', null, [slotElement]);
      svgElement = createElement('g', { attrs: { cursor: 'pointer' } }, [...polygons, foreignObjectElement]);
    } else {
      svgElement = createElement('g', { attrs: { cursor: 'pointer' } }, polygons);
    }

    return createElement('svg', { style: { display: 'none' } }, [svgElement]);
  },
  mounted () {
    const gEl = this.$el.children[0];
    gEl.remove();
    this.$el.remove();

    this.mapObject = L.customSVG(gEl, this.trackerBounds, this.sizeAtRest, { angle: this.angle });

    // Set up events for mobile and pc devices
    if (this.$feature.mobile) {
      this.mapObject.on('click', e => {
        if (!this.infoActive) this.$emit('click', e, this.tracker);
      });
      this.mapObject.on('contextmenu', e => {
        this.$emit('contextmenu', e);
      });
    } else {
      this.mapObject.on('click', e => this.$emit('click', e));
      this.mapObject.on('mouseover', e => this.$emit('mouseover'));
      this.mapObject.on('mouseout', e => this.$emit('mouseout'));
      this.mapObject.on('contextmenu', e => {
        if (!this.infoActive) this.$emit('contextmenu', e);
      });
    }

    this.updatePanelView();

    this.parentContainer = findRealParent(this.$parent);
    this.parentContainer.addLayer(this, false);
    this.ready = true;
  },
  beforeDestroy () {
    this.parentContainer.removeLayer(this);
  },
  watch: {
    viewState() {
      this.updatePanelView();
    },
    trackerStatus() {
      this.updatePanelView();
    }
  }
};
</script>
